import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import FaLock from 'react-icons/lib/fa/lock';
import styled from 'styled-components';

import {
  fetchReport,
  setEditing,
  clearEditing,
  setReportEditable,
  saveReport,
  authenticate,
  onLogin,
  getChats,
  fetchAuthenticationForms,
  uploadPortfolioImage,
  fetchPortfolio,
  savePortfolio,
  updatePortfolioField,
  addPortfolioSlide,
  removePortfolioSlide,
  updatePortfolioCompanyField,
  uploadPortfolioCompanyLogo,
  recoverSlides,
  saveKeywords,
  updateKeywords,
  fetchKeywords,
  saveCompanyRegions,
  fetchCompanyRegions,
  onEnablePortfolio,
  onDisablePortfolio,
  onShowPortfolio,
  onHidePortfolio,
} from '~actions'; // eslint-disable-line

import { showPaidFeatureControls } from '../../selectors';

// Components
import ControlButtons from './ControlButtons';
import ActionButtons from './ActionButtons';
import ChatList from '../chat/ChatList';
import AuthHeader from '../user/AuthHeader';
import ReportSummary from '../report/ReportSummary';
import ReportContacts from '../report/ReportContacts';
import Portfolio from '../paid/PaidFeatures';
import PortfolioSlides from '../paid/PortfolioSlides';
import PortfolioCompany from '../paid/PortfolioCompany';
import CompanyKeywords from '../paid/CompanyKeywords';
import CompanyLocalities from '../paid/CompanyLocalities';
import LicenseControlPanel from '../license/licensecontrolpanel.container';
import LoadingIndicator from '../../components/LoadingIndicator';

const propTypes = {
  match: PropTypes.object, // Injected by React Router
  report: PropTypes.object.isRequired,
  reportEditable: PropTypes.object,
  fetchReport: PropTypes.func.isRequired,
  setEditing: PropTypes.func.isRequired,
  clearEditing: PropTypes.func.isRequired,
  saveReport: PropTypes.func.isRequired,
  setReportEditable: PropTypes.func.isRequired,
  authenticate: PropTypes.func.isRequired,
  fetchAuthenticationForms: PropTypes.func.isRequired,
  getChats: PropTypes.func.isRequired,
  review: PropTypes.object.isRequired,
  session: PropTypes.object.isRequired,
  dashboard: PropTypes.object.isRequired,
  onLogin: PropTypes.func.isRequired,
  uploadPortfolioImage: PropTypes.func.isRequired,
  fetchPortfolio: PropTypes.func.isRequired,
  savePortfolio: PropTypes.func.isRequired,
  addPortfolioSlide: PropTypes.func.isRequired,
  removePortfolioSlide: PropTypes.func.isRequired,
  saveKeywords: PropTypes.func.isRequired,
  updatePortfolioField: PropTypes.func.isRequired,
  updatePortfolioCompanyField: PropTypes.func.isRequired,
  uploadPortfolioCompanyLogo: PropTypes.func.isRequired,
  recoverSlides: PropTypes.func.isRequired,
  onEnablePortfolio: PropTypes.func.isRequired,
  onDisablePortfolio: PropTypes.func.isRequired,
  onShowPortfolio: PropTypes.func.isRequired,
  onHidePortfolio: PropTypes.func.isRequired,
  portfolio: PropTypes.object.isRequired,
  slidesBackup: PropTypes.array.isRequired,
  keywords: PropTypes.array.isRequired,
  keywordsEditable: PropTypes.array.isRequired,
  updateKeywords: PropTypes.func.isRequired,
  fetchKeywords: PropTypes.func.isRequired,
  companyLocalities: PropTypes.array.isRequired,
  companyRegions: PropTypes.object.isRequired,
  fetchCompanyRegions: PropTypes.func.isRequired,
  saveCompanyRegions: PropTypes.func.isRequired,
  localities: PropTypes.object,
  regions: PropTypes.object,
  locale: PropTypes.object.isRequired,
  showPaidControls: PropTypes.bool.isRequired,
};


const Gate = styled.div`
  padding-top: 50px;
  position: relative;
  mouse-events: none;
`;

const Disabled = styled.div`
  align-items: flex-start;
  background-color: hsla(0, 0%, 100%, 0.75);
  bottom: 0;
  cursor: not-allowed;
  display: flex;
  justify-content: center;
  left: -100%;
  padding-top: 72px;
  position: absolute;
  right: -100%;
  top: 0;
  z-index: 9999;
`;

const Locked = styled(FaLock)`
  font-size: 144pt;
`;


const Gatekeeper = ({ isAuthenticated, children }) => {
  if (isAuthenticated) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  return (
    <React.Fragment>
      <Gate>
        <Disabled>
          <Locked />
        </Disabled>
        {children}
      </Gate>
    </React.Fragment>
  );
}

Gatekeeper.propTypes = {
  children: PropTypes.array.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
}

// TODO keep editables in state, not in store
class CompanyDashboard extends Component {
  static isAuthorized = (routerProps, session) => {
    const ALLOWED = ['license_retailer', 'license_admin', 'super_user'];
    if (session.invalidLogin) {
      return false;
    }
    const { logins } = session;
    if (!logins.tilaajavastuu) {
      return false;
    }
    const { tilaajavastuu: { client } } = logins;
    if (!client) {
      return false;
    }
    if (client.super_user) {
      return true;
    }
    const matchinLevels = client.access_levels.filter(
      ual => ALLOWED.indexOf(ual) !== -1
    );
    if (matchinLevels.length) {
      return true;
    }
    const { company } = client;
    const { match: { params: { country, identifier } } } = routerProps;
    if (country === company.country && identifier === company.identifier) {
      return true;
    }
    return false;
  }

  constructor(props) {
    super(props);

    const {
      companyRegions,
      match: {
        params: {
          country,
          identifier,
        },
      },
    } = this.props;

    this.state = { companyRegions };
    this.updateContext(country, identifier);
    this.props.getChats();
  }

  componentDidUpdate(prevProps) {
    const {
      match: { params },
      dashboard: { editMode },
      companyRegions,
    } = this.props;
    const {
      match: { params: prevParams },
      dashboard: { editMode: prevEditMode },
      companyRegions: prevCompanyRegions,
    } = prevProps;

    if (params.country !== prevParams.country
      || params.identifier !== prevParams.identifier) {
      // Company has changed, update context.
      this.updateContext(params.country, params.identifier);
    } else if ((editMode !== prevEditMode)
      || (Object.values(companyRegions).length
        && !Object.values(prevCompanyRegions).length)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ companyRegions });
    }
  }

  componentWillUnmount() {
    this.handleClear();
  }

  onEnablePortfolio = () => {
    const { match: { params } } = this.props;
    const { country, identifier } = params;

    this.props.onEnablePortfolio({ country, identifier });
  };

  onDisablePortfolio = () => {
    const { match: { params } } = this.props;
    const { country, identifier } = params;

    this.props.onDisablePortfolio({ country, identifier });
  };

  onShowPortfolio = () => {
    const { match: { params } } = this.props;
    const { country, identifier } = params;

    this.props.onShowPortfolio({ country, identifier });
  };

  onHidePortfolio = () => {
    const { match: { params } } = this.props;
    const { country, identifier } = params;

    this.props.onHidePortfolio({ country, identifier });
  };

  updateContext = (country, identifier) => {
    this.props.fetchReport({ country, identifier });
    this.props.fetchPortfolio({ country, identifier });
    this.props.fetchKeywords({ country, identifier });
    this.props.fetchCompanyRegions({ country, identifier });
  }

  handleClear = () => {
    const { slidesBackup } = this.props;
    if (slidesBackup.length > 0) {
      this.props.recoverSlides();
    }

    this.props.clearEditing();
  };

  handleSave = () => {
    const {
      match: { params },
      portfolio,
      keywordsEditable,
      companyRegions: oldCompanyRegions } = this.props;
    const { companyRegions } = this.state;
    const { country, identifier } = params;

    // Save portfolio
    this.props.savePortfolio({
      meta: { country, identifier },
      data: portfolio,
    });

    // Save report
    this.props.saveReport();

    // Save keywords
    const keywords = keywordsEditable.reduce((acc, keyword) => {
      const trimmed = keyword.value.trim();
      if (trimmed) acc.push(trimmed);
      return acc;
    }, []);
    this.props.saveKeywords({
      country,
      identifier,
      keywords,
    });

    // Save localities
    this.props.saveCompanyRegions({
      country,
      identifier,
      regions: companyRegions,
      oldRegions: oldCompanyRegions
    });
  };

  updateCompanyRegions = companyRegions => {
    this.setState({ companyRegions });
  }

  authorizeUser = accessList => {
    const { session } = this.props;
    if (!session) {
      return false;
    }
    if (session.invalidLogin) {
      return false;
    }
    const { logins } = session;
    if (!logins.tilaajavastuu) {
      return false;
    }
    const { tilaajavastuu: { client: { access_levels: ual } } } = logins;
    const _accessList = [...accessList, 'super_user'];
    const authorizedAccess = _accessList.filter(a => ual.indexOf(a) !== -1);
    if (authorizedAccess.length) {
      return true;
    }
    return false;
  }

  render() {
    const {
      session,
      report,
      dashboard,
      reportEditable,
      review,
      match,
      portfolio,
      locale,
      showPaidControls,
    } = this.props;



    const { editMode, chats, authenticationForms } = dashboard;

    const editablesProps = { dashMode: true, editMode, review, match };
    const commonProps = {
      onLogin: this.props.onLogin,
      setReportEditable: this.props.setReportEditable,
      session,
      dashboard,
      report,
      reportEditable,
      locale,
    };

    const { tilaajavastuu } = session.logins;
    const isAuthenticated = tilaajavastuu && tilaajavastuu.client.verified;

    const loadingIndicator = (
      <div className="CompanyDashboard">
        <div style={{ height: 30 }} />
        <div className="main pure-g">
          <div
            className="pure-u-1 no-print"
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <LoadingIndicator iconSize='large' />
          </div>
        </div>
      </div>
    );
    // These if conditions could be crammed into one, but for the sake of some
    // clarity check report in general and reportEditable only when edit mode is
    // toggled on.
    if (!report
      || match.params.country !== report.country
      || match.params.identifier !== report.identifier) {
      return loadingIndicator;
    } else if (editMode
      && (!reportEditable
        || match.params.country !== reportEditable.country
        || match.params.identifier !== reportEditable.identifier)) {
      return loadingIndicator;
    }

    return (
      <div className="CompanyDashboard">
        <div style={{ height: 30 }} />
        <div className="main pure-g">
          {!isAuthenticated && (
            <div className="pure-u-1">
              <AuthHeader
                authHandler={this.props.authenticate}
                fetchAuthenticationForms={this.props.fetchAuthenticationForms}
                authenticationForms={authenticationForms}
              />
            </div>
          )}

          {isAuthenticated && (
            <div className="pure-u-1 pure-u-23-24">
              <ControlButtons
                setEditMode={this.props.setEditing}
                editMode={editMode}
              />
            </div>
          )}

          <Gatekeeper isAuthenticated={isAuthenticated}>
            <div className="pure-u-1-24 hidden-mobile no-print" />

            <div className="pure-u-1-1 pure-u-lg-15-24">

              <ReportSummary {...commonProps} {...editablesProps} />

              <div style={{ height: 20 }} />

              <ReportContacts {...commonProps} {...editablesProps} />

              <div style={{ height: 20 }} />

              <Portfolio>
                {!showPaidControls ? (
                  <FormattedHTMLMessage
                    id='portfolio.dashboard.disabled'
                    description={'Descripton when brochure is disabled.'}
                    defaultMessage='<p>Your companys brochure is disabled</p>'
                  />
                ) : (
                  <React.Fragment>
                    <CompanyKeywords
                      editMode={editMode}
                      handleUpdate={this.props.updateKeywords}
                      keywords={
                        editMode
                          ? this.props.keywordsEditable
                          : this.props.keywords
                      }
                    />
                    <CompanyLocalities
                      editMode={editMode}
                      localities={this.props.localities}
                      regions={this.props.regions}
                      locale={this.props.locale}
                      handleUpdate={this.updateCompanyRegions}
                      reportEditable={reportEditable}
                      setReportEditable={this.props.setReportEditable}
                      companyRegions={this.state.companyRegions}
                      companyLocalities={this.props.companyLocalities}
                    />
                    <PortfolioCompany
                      editMode={editMode}
                      company={portfolio.company || null}
                      identifier={report.identifier}
                      country={report.country}
                      hidden={report.portfolio_hidden}
                      showPortfolio={this.onShowPortfolio}
                      hidePortfolio={this.onHidePortfolio}
                      handleChange={this.props.updatePortfolioCompanyField}
                      handleLogoUpload={this.props.uploadPortfolioCompanyLogo}
                    />
                    <PortfolioSlides
                      editMode={editMode}
                      slides={portfolio.slides || []}
                      identifier={report.identifier}
                      country={report.country}
                      addSlide={this.props.addPortfolioSlide}
                      removeSlide={this.props.removePortfolioSlide}
                      handleChange={this.props.updatePortfolioField}
                      handleImageUpload={this.props.uploadPortfolioImage}
                    />
                  </React.Fragment>
                )}
              </Portfolio>
            </div>

            <div className="pure-u-1 pure-u-lg-7-24">
              <ChatList chats={chats} />
              {this.authorizeUser(['license_admin', 'license_retailer']) && (
                <LicenseControlPanel editMode={editMode} />
              )}
            </div>

            {editMode && (
              <div className="pure-u-1" style={{ height: 80 }} />
            )}

            {editMode && (
              <ActionButtons
                position="bottom"
                clearAction={this.handleClear}
                confirmAction={this.handleSave}
                clearLabel={
                  <FormattedMessage
                    id="action.cancel"
                    description="Label for cancel button"
                    defaultMessage="Cancel"
                  />
                }
                confirmLabel={
                  <FormattedMessage
                    id="action.save"
                    description="Label for save button"
                    defaultMessage="Save"
                  />
                }
              />
            )}
          </Gatekeeper>
        </div>
      </div>
    );
  }
}

CompanyDashboard.propTypes = propTypes;

// This makes state objects available to the component via props!
function mapStateToProps(state) {
  return {
    reportEditable: state.reportEditable,
    report: state.report,
    review: state.review,
    session: state.session,
    dashboard: state.dashboard,
    portfolio: state.portfolio.portfolio,
    slidesBackup: state.portfolio.slidesCopy,
    keywordsEditable: state.paidFeatures.keywordsEditable,
    keywords: state.paidFeatures.keywords,
    companyLocalities: state.paidFeatures.localities,
    companyRegions: state.paidFeatures.regions,
    localities: state.localities.data,
    regions: state.regions.data,
    locale: state.locale,
    showPaidControls: showPaidFeatureControls(state),
  };
}

// This adds action creators to components props
function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchReport,
      setReportEditable,
      setEditing,
      clearEditing,
      saveReport,
      authenticate,
      fetchAuthenticationForms,
      onLogin,
      getChats,
      uploadPortfolioImage,
      fetchPortfolio,
      savePortfolio,
      addPortfolioSlide,
      removePortfolioSlide,
      updatePortfolioField,
      updatePortfolioCompanyField,
      uploadPortfolioCompanyLogo,
      recoverSlides,
      onEnablePortfolio,
      onDisablePortfolio,
      onShowPortfolio,
      onHidePortfolio,
      updateKeywords,
      saveKeywords,
      fetchKeywords,
      saveCompanyRegions,
      fetchCompanyRegions,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDashboard);
